import { useQuery } from '@tanstack/react-query'
import {
  Users,
  FolderKanban,
  TrendingUp,
  TrendingDown,
  Activity,
  CheckCircle2,
  UserPlus,
  Bell,
} from 'lucide-react'
import { TodayPriorities } from './TodayPriorities'
import { MyContents } from './MyContents'
import { TodoWidget } from './TodoWidget'
import { StatCard } from '@/components/common/StatCard'
import { Card, CardHeader } from '@/components/common/Card'
import { Badge } from '@/components/common/Badge'
import { ErrorState } from '@/components/common/States'
import { DashboardSkeleton } from './DashboardSkeleton'
import { apiClient } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'

interface AdminDashboardData {
  kpis: {
    totalClients?: number
    activeClients?: number
    newClientsThisMonth?: number
    totalProjects?: number
    activeProjects?: number
    completedProjects?: number
    totalTasks?: number
    completedTasks?: number
    overdueTasks?: number
    userCount?: number
    activeUserCount?: number
    teamMembers?: number
  }
  systemHealth?: {
    database?: string
    clientsAtRisk?: { id: string; name: string; health?: number; healthScore?: number }[]
    overdueProjects?: { id: string; name: string; client?: string | { id: string; name: string } }[]
    blockedTasks?: { id: string; title: string; project?: string | { id: string; name: string } }[]
  }
  trends?: {
    activeClients?: number | null
    activeProjects?: number | null
    completedTasks?: number | null
  }
  recentAuditLog?: {
    id?: string
    action?: string
    entity?: string
    entityId?: string
    user?: { firstName?: string; lastName?: string }
    createdAt?: string
    changes?: { name?: string; title?: string; [key: string]: any }
    [key: string]: any
  }[]
  recentNotifications?: {
    id?: string
    type?: string
    title?: string
    message?: string
    isRead?: boolean
    entityType?: string
    entityId?: string
    user?: { firstName?: string; lastName?: string }
    createdAt?: string
  }[]
  charts?: Record<string, any>
  unassignedTasks?: {
    id: string
    title: string
    status: string
    priority: string
    dueDate?: string | null
    project?: string | null
    client?: string | null
    createdBy?: string | null
    createdAt: string
  }[]
}

function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const diffSec = Math.max(0, Math.round(diffMs / 1000))
  if (diffSec < 60) return 'ora'
  const diffMin = Math.round(diffSec / 60)
  if (diffMin < 60) return `${diffMin} min fa`
  const diffHr = Math.round(diffMin / 60)
  if (diffHr < 24) return `${diffHr} h fa`
  return new Date(timestamp).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
}

// Indicatore di trend compatto per gli hint delle StatCard (su/giù + % + label).
function TrendHint({ value, label }: { value: number; label: string }) {
  const positive = value >= 0
  const Icon = positive ? TrendingUp : TrendingDown
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className={cn('w-3.5 h-3.5', positive ? 'text-success-500' : 'text-danger-500')} />
      <span className={cn('font-medium tabular-nums', positive ? 'text-success-600' : 'text-danger-600')}>
        {positive ? '+' : '-'}{Math.abs(value)}%
      </span>
      <span className="text-slate-400">{label}</span>
    </span>
  )
}

export function AdminDashboard() {
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()

  const { data, isLoading, isError, refetch, dataUpdatedAt, isFetching } = useQuery<AdminDashboardData>({
    queryKey: ['dashboard', 'admin'],
    queryFn: async () => {
      const res = await apiClient.get<any>('/dashboard')
      const outer = res?.data ?? res
      const inner = outer?.data ?? outer
      return (inner ?? {}) as AdminDashboardData
    },
    refetchInterval: 60_000,
    retry: false,
  })

  if (isLoading && !data) return <DashboardSkeleton />

  // Errore senza alcun dato in cache: mostra uno stato d'errore esplicito
  // invece di degradare su empty-state "tutto in ordine" (fuorviante).
  if (isError && !data) {
    return (
      <Card padding="md" className="border-slate-200">
        <ErrorState
          title="Impossibile caricare la dashboard"
          description="Si è verificato un errore nel recupero dei dati. Riprova."
          onRetry={() => { void refetch() }}
        />
      </Card>
    )
  }

  const d = data ?? { kpis: {} } as AdminDashboardData

  // Etichetta freschezza dati basata sull'ultimo fetch riuscito.
  const updatedLabel = isError
    ? 'Aggiornamento fallito'
    : dataUpdatedAt
      ? `Aggiornato ${formatRelativeTime(dataUpdatedAt)}`
      : 'Aggiornato ora'

  // Map recentAuditLog → activity format
  const activityItems = (d.recentAuditLog ?? []).map((log, idx) => {
    const entityName = log.changes?.name ?? log.changes?.title ?? ''
    const entityLabel = log.entity ?? ''
    const target = entityName
      ? `${entityLabel} "${entityName}"`
      : [entityLabel, log.entityId].filter(Boolean).join(' #')
    return {
      id: log.id ?? String(idx),
      actor: [log.user?.firstName, log.user?.lastName].filter(Boolean).join(' ') || 'Sistema',
      action: log.action ?? '',
      target,
      timestamp: (log.createdAt ?? log.timestamp)
        ? new Date(log.createdAt ?? log.timestamp).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
        : '',
    }
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Benvenuto, {user?.firstName}. Ecco il riepilogo di oggi.
          </p>
        </div>
        <span className={cn('text-xs mt-1 flex-shrink-0', isError ? 'text-danger-600 font-medium' : 'text-slate-400')}>
          {isFetching && !isError ? 'Aggiornamento…' : updatedLabel}
        </span>
      </div>

      {/* Today's Priorities + To-Do side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TodayPriorities />
        <TodoWidget />
      </div>

      {/* My Contents - stato contenuti PED */}
      <MyContents />

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Clienti Attivi"
          value={d.kpis?.activeClients ?? 0}
          icon={Users}
          tone="primary"
          {...(d.trends?.activeClients != null ? { hint: <TrendHint value={d.trends.activeClients} label="vs mese scorso" /> } : {})}
        />
        <StatCard
          label="Progetti Attivi"
          value={d.kpis?.activeProjects ?? 0}
          icon={FolderKanban}
          tone="success"
          {...(d.trends?.activeProjects != null ? { hint: <TrendHint value={d.trends.activeProjects} label="vs mese scorso" /> } : {})}
        />
        <StatCard
          label="Team Members"
          value={d.kpis?.teamMembers ?? d.kpis?.userCount ?? 0}
          icon={TrendingUp}
          tone="neutral"
        />
        <StatCard
          label="Progetti Completati"
          value={d.kpis?.completedProjects ?? 0}
          icon={Activity}
          tone="warning"
        />
      </div>

      {/* System health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="md" className="border-slate-200">
          <CardHeader title="Salute Sistema" description="Elementi che richiedono attenzione" />
          <div className="space-y-4">
            {/* Clients at risk */}
            {(() => {
              const allClients = d.systemHealth?.clientsAtRisk ?? []
              const activeRisk = allClients.filter((c) => {
                const score = c.health ?? (c as any).healthScore ?? 0
                return score > 0 && score < 50
              })
              const dormant = allClients.filter((c) => {
                const score = c.health ?? (c as any).healthScore ?? 0
                return score === 0
              })

              return (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Stato Clienti
                  </p>
                  <div className="space-y-1.5">
                    {activeRisk.length > 0 && (
                      <>
                        <p className="text-[11px] text-warning-600 font-medium uppercase tracking-wide">In calo</p>
                        {activeRisk.map((client) => {
                          const healthValue = client.health ?? (client as any).healthScore ?? 0
                          const barColor = healthValue >= 30 ? 'bg-warning-500' : 'bg-danger-500'
                          return (
                            <div key={client.id} className="flex items-center justify-between text-sm">
                              <span className="text-slate-700">{client.name ?? 'N/A'}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className={`h-full ${barColor} rounded-full`} style={{ width: `${healthValue}%` }} />
                                </div>
                                <span className="text-xs text-warning-600 font-medium w-8 text-right tabular-nums">{healthValue}%</span>
                              </div>
                            </div>
                          )
                        })}
                      </>
                    )}
                    {dormant.length > 0 && (
                      <div className="mt-2 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-500">
                            <span className="font-semibold text-slate-700 tabular-nums">{dormant.length}</span> clienti senza attività recente
                          </span>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">Nessun progetto/preventivo attivo</span>
                        </div>
                      </div>
                    )}
                    {activeRisk.length === 0 && dormant.length === 0 && (
                      <div className="flex items-center gap-2 text-sm text-success-600">
                        <span className="w-2 h-2 rounded-full bg-success-500 flex-shrink-0" />
                        Tutti i clienti sono in buona salute
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

            {/* Overdue projects */}
            {(d.systemHealth?.overdueProjects ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Progetti in Ritardo
                </p>
                <div className="space-y-1.5">
                  {(d.systemHealth?.overdueProjects ?? []).map((project) => (
                    <div key={project.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 truncate">{project.name ?? 'N/A'}</span>
                      {project.client && (
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {typeof project.client === 'string' ? project.client : project.client.name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blocked tasks */}
            {(d.systemHealth?.blockedTasks ?? []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Task Bloccati
                </p>
                <div className="space-y-1.5">
                  {(d.systemHealth?.blockedTasks ?? []).map((task) => (
                    <div key={task.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 truncate">{task.title ?? 'N/A'}</span>
                      {task.project && (
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {typeof task.project === 'string' ? task.project : task.project.name}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All clear */}
            {(d.systemHealth?.clientsAtRisk ?? []).length === 0 &&
             (d.systemHealth?.overdueProjects ?? []).length === 0 &&
             (d.systemHealth?.blockedTasks ?? []).length === 0 && (
              <div className="flex items-center gap-2 text-sm text-success-600 py-2">
                <CheckCircle2 className="w-4 h-4" />
                Tutto in ordine
              </div>
            )}
          </div>
        </Card>

        {/* Unassigned tasks (inline in the grid) */}
        <Card padding="md" className="border-slate-200">
          <CardHeader
            title="Task da Assegnare"
            description={`${(d.unassignedTasks ?? []).length} task senza assegnatario`}
            action={
              (d.unassignedTasks ?? []).length > 0 ? (
                <Badge variant="warning" dot>
                  {(d.unassignedTasks ?? []).length}
                </Badge>
              ) : null
            }
          />
          <div className="space-y-1">
            {(d.unassignedTasks ?? []).slice(0, 10).map((task) => (
              <button
                key={task.id}
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                onClick={() => navigate(`/tasks?taskId=${task.id}`)}
                aria-label={`Apri task ${task.title}`}
              >
                <div
                  className={cn(
                    'w-1.5 h-8 rounded-full flex-shrink-0',
                    task.priority === 'URGENT' ? 'bg-danger-500' :
                    task.priority === 'HIGH' ? 'bg-warning-500' :
                    task.priority === 'MEDIUM' ? 'bg-primary-400' : 'bg-slate-200'
                  )}
                />
                <UserPlus className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{task.title}</p>
                  <p className="text-xs text-slate-400">
                    {[task.client, task.project].filter(Boolean).join(' · ') || 'Nessun progetto'}
                    {task.createdBy ? ` · Creato da ${task.createdBy}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {task.dueDate && (
                    <span className={cn(
                      'text-xs',
                      new Date(task.dueDate) < new Date() ? 'text-danger-600 font-medium' : 'text-slate-400'
                    )}>
                      {new Date(task.dueDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                  <Badge
                    variant={
                      task.priority === 'URGENT' ? 'danger' :
                      task.priority === 'HIGH' ? 'warning' :
                      'neutral'
                    }
                    size="sm"
                  >
                    {task.priority === 'URGENT' ? 'Urgente' :
                     task.priority === 'HIGH' ? 'Alta' :
                     task.priority === 'MEDIUM' ? 'Media' : 'Bassa'}
                  </Badge>
                </div>
              </button>
            ))}
            {(d.unassignedTasks ?? []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">
                Tutti i task sono assegnati
              </p>
            )}
          </div>
        </Card>
      </div>

      {/* Activity feed + Notifications side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="md" className="border-slate-200">
          <CardHeader
            title="Attivita' Recente"
            description="Ultime azioni del team"
          />
          <div className="space-y-0">
            {activityItems.map((item, index) => (
              <div
                key={item.id}
                className={cn(
                  'flex items-center gap-3 py-2.5 text-sm',
                  index < activityItems.length - 1 && 'border-b border-slate-200'
                )}
              >
                <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-slate-600">
                    {(item.actor ?? '?').charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-700">{item.actor ?? 'Utente'}</span>{' '}
                  <span className="text-slate-500">{item.action ?? ''}</span>{' '}
                  <span className="font-medium text-slate-700">{item.target ?? ''}</span>
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0">{item.timestamp ?? ''}</span>
              </div>
            ))}
            {activityItems.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">
                Nessuna attivita' recente
              </p>
            )}
          </div>
        </Card>

        <Card padding="md" className="border-slate-200">
          <CardHeader
            title="Notifiche"
            description="Ultime notifiche di sistema"
            action={
              (d.recentNotifications ?? []).filter(n => !n.isRead).length > 0 ? (
                <Badge variant="danger" dot>
                  {(d.recentNotifications ?? []).filter(n => !n.isRead).length} nuove
                </Badge>
              ) : null
            }
          />
          <div className="space-y-0">
            {(d.recentNotifications ?? []).map((n, index) => (
              <div
                key={n.id ?? index}
                className={cn(
                  'flex items-start gap-3 py-2.5 text-sm',
                  index < (d.recentNotifications ?? []).length - 1 && 'border-b border-slate-200',
                  !n.isRead && 'bg-primary-50 -mx-3 px-3 rounded-xl'
                )}
              >
                <div className={cn(
                  'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                  !n.isRead ? 'bg-primary-50' : 'bg-slate-100'
                )}>
                  <Bell className={cn('w-3.5 h-3.5', !n.isRead ? 'text-primary-600' : 'text-slate-400')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm truncate', !n.isRead ? 'font-medium text-slate-800' : 'text-slate-600')}>
                    {n.title}
                  </p>
                  {n.message && (
                    <p className="text-xs text-slate-400 truncate mt-0.5">{n.message}</p>
                  )}
                </div>
                <span className="text-xs text-slate-400 flex-shrink-0 mt-0.5">
                  {n.createdAt
                    ? new Date(n.createdAt).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
                    : ''}
                </span>
              </div>
            ))}
            {(d.recentNotifications ?? []).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">
                Nessuna notifica recente
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
